/*!
 * stereo-content.js — what the two channels actually CONTAIN.
 *
 * Every tool in the mono/stereo family decided everything from
 * `audioBuffer.numberOfChannels`. That number describes the container, not the
 * signal, and the two disagree constantly:
 *
 *   • a voice memo exported "in stereo" carries one signal twice — the channel
 *     count says stereo, the content is mono;
 *   • a file with a dead channel says stereo and plays out of one ear;
 *   • a polarity-flipped master says stereo and DISAPPEARS when summed to mono.
 *
 * Trusting the count produced one silent-damage bug and one dead end (measured
 * 2026-08-13, see the tool memory). This module answers the question the tools
 * were actually asking.
 *
 *   window.StereoContent.analyse(audioBuffer) → report
 *
 * ── Thresholds ──────────────────────────────────────────────────────────
 * Every constant below was picked from measured distributions over 34 real
 * tracks (public/audio/ai-styles) × several derived populations — not chosen
 * for roundness. `scripts/stereo-content-calibration.mjs` is the script that
 * produced them and the numbers quoted in each comment are its output. Re-run
 * it before moving any of them.
 *
 * `scripts/stereo-content-validate.mjs` is the other half: it runs THIS file
 * (not a re-derivation of its math) over real tracks at full length, so the
 * stride path is exercised, and exits 1 if any population lands on the wrong
 * side. Run it after any change here.
 */
(function () {
    'use strict';

    /* Side energy below which the two channels are "the same signal".
       Measured: dual-mono through wav / mp3-128 / opus-96 lands at -180…-59 dB.
       Genuine stereo (incl. a deliberately narrow 5% mix) never goes below
       -43 dB. -60 dB sits in the clear air between them: 0 false positives on
       68 stereo files, 68/68 lossless+mp3 dual-mono caught.
       ⚠️ AAC used to be the known miss: it re-quantises each channel
       independently, so a mono source saved as 2-channel AAC lands at
       -20…-69 dB of side, right on top of the narrow-stereo population, and no
       LEVEL separates the two. That is still true — see the structure test
       below, which separates them by what the side IS, not how loud it is. */
    var SIDE_MONO_DB = -60;

    /* ── Codec residue vs a genuinely quiet side (2026-09-13) ───────────────
       A level cannot tell a narrow real mix from a dual-mono file an AAC
       encoder has roughened: both sit at -20…-40 dB of side. Two facts about
       the side itself can, and they were measured, not assumed — 16 real
       tracks (public/audio/ai-styles) × narrow 15% / 5% mixes × dual-mono AAC
       at 48/64/96/128 k, music AND voice, plus a voice panned 52/48…60/40:

         population                side dB        |corr(S,M)|   rho1 of side
         genuine stereo            -13.9 … -6.4   0.009…0.117   0.935…0.999
         narrow 15% / 5%           -30.2 … -31.4  0.009…0.117   0.935…0.999
         dual-mono AAC (music)     -69   … -22.6  0.000…0.001  -0.193…0.601
         dual-mono AAC (voice)     -49.7 … -20.4  0.000…0.001   0.538…0.769
         voice panned 52/48…60/40  -22.3 … -13.7  0.475…0.926   0.642…0.899

       • rho1 — how much each side sample predicts the next. A real side is
         music and moves smoothly (≥ 0.935); quantisation noise does not
         (≤ 0.769). Separates narrow MUSIC from residue.
       • |corr(S,M)| — whether the side is a scaled copy of the middle. Panning
         a voice makes it one (≥ 0.475); codec noise is unrelated to what it
         rides on (≤ 0.001). Separates a PANNED VOICE from residue, which rho1
         alone cannot (0.642 vs 0.769 overlap).
       Neither alone is enough; together they classify all 13 populations with
       margin (rho1 gap 0.77→0.94, corr gap 0.001→0.48).

       ⚠️ rho1 is a statistic of CONSECUTIVE samples. The scan below strides on
       long files, and a stride-to-stride product is no longer lag-1 — for
       music it collapses toward the residue range. Each sampled position reads
       its own predecessor instead.
       ⚠️ Failure direction is safe by construction: at 96 kHz a codec's
       residue is smoother, rho1 rises, and such a file is simply reported as
       stereo — exactly what happened before this test existed. A real stereo
       side can never be pushed the other way, because music only gets smoother
       with a higher rate.

       Measured reach before the change, RU shard, 90 days: 70 sessions were
       refused as "real stereo" with a side quieter than -16 dB, against 54
       dual-mono sessions accepted — more dual-mono-looking files were turned
       away than let in. */
    var STRUCT_SIDE_DB = -18;      // louder than this is always real stereo (loudest real: -6.4, quietest real full mix: -13.9)
    var RESIDUE_RHO1 = 0.9;        // real side ≥ 0.935, residue ≤ 0.769
    var RESIDUE_SM_CORR = 0.3;     // panned voice ≥ 0.475, residue ≤ 0.001

    /* One channel effectively dead ("sound only in my left ear").
       Measured: 34/34 detected, 0 false positives on genuine stereo.
       ⚠️ Those 34 dead channels were all DIGITAL silence (-182 dB). See the
       floor test below for the dead channel a real input actually records. */
    var QUIET_CH_DB = -60;

    /* ── A dead channel that is not digital silence (2026-10-15) ────────────
       A real unused input does not record zeros: it records its own floor —
       preamp hiss, room, mains hum — at -60…-90 dBFS. Against speech at an
       ordinary -26…-42 dBFS that floor sits only 15…55 dB under the programme,
       nowhere near -60, and every such file read as REAL STEREO. Report
       09.10 (/mono-to-stereo, iPhone .mov, "просто сделал тишину"): voice in
       the right channel, the left peaking at -63 dBFS, verdict "stereo".
       Measured (scripts/stereo-content-dead-channel-calibration.mjs — real
       speech, TTS speech and music, under a floor of real room noise, pink
       noise and 50 Hz hum at -60…-90 dBFS, raw and through AAC 128k), the
       quieter channel's 50 ms levels, p90 − p10:

         population                         quietCh dB      spread    env corr
         genuine stereo, 34 real tracks     -0.3 …  -0.0    5.7…20.0  0.80…1.00
         dead channel: a floor             -57   … -15      0.0…8.0  -0.20…0.24
         second voice, 0…50 dB quieter     -45.5 …  -0.0   13.3…51.7 -0.01…0.11
         bleed of the loud channel         -45.5 … -27.0   12.5…29.1  0.66…0.96

       • spread — a floor does not move (≤ 8.0 dB); a voice of its own pauses
         and speaks (≥ 13.3, and that one 50 dB under the host, at -76 dBFS);
       • envelope correlation — bleed rises and falls with the loud channel
         (≥ 0.66); an independent voice does not (≤ 0.11).
       Either signature means the quieter channel carries no programme of its
       own. Both only below FLOOR_GAP_DB: music moves as little as 5.7 dB and
       genuine stereo is never more than 0.3 dB out of balance, so the gap is
       what keeps a real mix out (≈ 20 dB of clear air).

       ── Lopsided: past the gap, whatever the quiet channel holds (2026-10-29) ──
       A second voice past the gap stays kind 'stereo' — it IS a programme of
       its own, and /stereo-to-mono must sum it, not drop it for the loud
       channel. But it is not a stereo IMAGE: a channel 20 dB under the
       programme leaves the whole file heard from one side, which is exactly
       what the listener came to fix. The report says so as `lopsided`, with
       quietIs 'own'. Field, /mono-to-stereo, 15…29.10: files refused as
       "real stereo" with the quiet channel 24…57 dB down — a second caller,
       or a dead channel that is digital zero with stray bursts (spread 55…92,
       no floor to measure) — while the page's own meters showed that side at
       -48…-66 dBFS under the refusal. */
    var FLOOR_GAP_DB = -20;
    var FLOOR_SPREAD_DB = 10;      // floor ≤ 8.0, a voice of its own ≥ 13.3
    var BLEED_ENV_CORR = 0.5;      // independent ≤ 0.11, bleed ≥ 0.66
    var ENV_FRAME_S = 0.05;
    var ENV_MAX_FRAMES = 3000;     // long files: evenly spaced frames, each read whole

    /* How much level a mono sum may lose before it is worth interrupting the
       user. Measured on real music: normal stereo loses 0.1…1.3 dB; a
       polarity-flipped master loses 5.9…17.2 dB; a single flipped element
       loses 1.8…7.4 dB. At -1.5 dB: 0 false alarms across 68 normal files,
       89/102 damaged files caught. */
    var SUM_LOSS_WARN_DB = -1.5;

    /* Negative correlation means the WHOLE file is polarity-flipped, which is
       the only case where re-summing with one channel inverted is the right
       repair. Measured: genuine stereo never drops below +0.489, so there is
       no overlap; -0.2 is a dead zone, not a tuned edge. */
    var POLARITY_CORR = -0.2;

    /* Long files are sampled rather than scanned. Measured drift between a full
       scan and strides of 4/16/64: 0.0085 correlation, 0.12 dB — far below any
       threshold above. 4M frames is ~90 s of 44.1 kHz audio at stride 1. */
    var MAX_FRAMES = 4e6;

    function db(v) { return v <= 1e-12 ? -200 : 20 * Math.log10(v); }

    /* What the quieter channel does over time, in 50 ms levels: how far it
       moves (p90 − p10) and whether it moves with the louder one (Pearson r of
       the two level tracks). Frames are spread evenly over a long file and
       each frame is read in full — a level is a statistic of neighbours. */
    function envelopeOf(Q, Lo, len, sampleRate) {
        var W = Math.max(64, Math.round((sampleRate || 44100) * ENV_FRAME_S));
        var nf = Math.floor(len / W);
        if (nf < 8) return null;
        var step = Math.max(1, Math.ceil(nf / ENV_MAX_FRAMES));
        var q = [], l = [];
        for (var f = 0; f < nf; f += step) {
            var sq = 0, sl = 0;
            for (var j = f * W, e = j + W; j < e; j++) { sq += Q[j] * Q[j]; sl += Lo[j] * Lo[j]; }
            // −150 dB floor: a run of exact zeros must not read as a cliff of −∞
            q.push(Math.max(-150, 10 * Math.log10(sq / W + 1e-30)));
            l.push(Math.max(-150, 10 * Math.log10(sl / W + 1e-30)));
        }
        var s = q.slice().sort(function (a, b) { return a - b; });
        var spread = s[Math.floor((s.length - 1) * 0.9)] - s[Math.floor((s.length - 1) * 0.1)];
        var n = q.length, mq = 0, ml = 0, i;
        for (i = 0; i < n; i++) { mq += q[i]; ml += l[i]; }
        mq /= n; ml /= n;
        var cov = 0, vq = 0, vl = 0;
        for (i = 0; i < n; i++) { cov += (q[i] - mq) * (l[i] - ml); vq += (q[i] - mq) * (q[i] - mq); vl += (l[i] - ml) * (l[i] - ml); }
        return { spread: spread, envCorr: (vq > 1e-9 && vl > 1e-9) ? cov / Math.sqrt(vq * vl) : 0 };
    }

    /**
     * @param {AudioBuffer} buffer
     * @returns {object|null} report, or null if there is nothing to analyse
     */
    function analyse(buffer) {
        if (!buffer || typeof buffer.getChannelData !== 'function') return null;
        var channels = buffer.numberOfChannels;
        if (channels < 1) return null;

        if (channels === 1) {
            return {
                channels: 1, kind: 'mono', isMonoContent: true,
                sumLossDb: 0, sideDb: -200, corr: 1, quietChDb: 0,
                bitExact: true, loudSide: null, monoSumSafe: true, polarityFlipped: false,
            };
        }

        var L = buffer.getChannelData(0);
        var R = buffer.getChannelData(1);
        var len = Math.min(L.length, R.length);
        if (len < 1) return null;

        var stride = Math.max(1, Math.ceil(len / MAX_FRAMES));
        var sl = 0, sr = 0, slr = 0, sMid = 0, sSide = 0, n = 0, maxDiff = 0;
        var sMidSide = 0, sLag = 0, sPrevSide = 0, sumPeak = 0;
        for (var i = 0; i < len; i += stride) {
            var l = L[i], r = R[i];
            sl += l * l; sr += r * r; slr += l * r;
            // The peak a unity L+R sum would reach — what a caller summing a
            // lopsided file needs to stay under full scale.
            var lr = l + r; if (lr < 0) lr = -lr; if (lr > sumPeak) sumPeak = lr;
            var mid = (l + r) * 0.5, side = (l - r) * 0.5;
            sMid += mid * mid; sSide += side * side;
            sMidSide += mid * side;
            // lag-1 against this position's OWN predecessor, not the previous
            // stride — see the note on rho1 above.
            if (i > 0) {
                var prevSide = (L[i - 1] - R[i - 1]) * 0.5;
                sLag += side * prevSide;
                sPrevSide += prevSide * prevSide;
            }
            var d = Math.abs(l - r); if (d > maxDiff) maxDiff = d;
            n++;
        }
        if (!n) return null;

        /* programme² = (L²+R²)/2 = mid² + side², so every level below is
           expressed against the same reference and the two add up. */
        var programme = Math.sqrt((sl + sr) / (2 * n));
        var midRms = Math.sqrt(sMid / n);
        var sideRms = Math.sqrt(sSide / n);
        var progDb = db(programme);

        var rep = {
            channels: channels,
            corr: slr / (Math.sqrt(sl * sr) + 1e-20),
            sumLossDb: db(midRms) - progDb,
            sideDb: db(sideRms) - progDb,
            quietChDb: Math.min(db(Math.sqrt(sl / n)), db(Math.sqrt(sr / n))) - progDb,
            bitExact: maxDiff === 0,
            loudSide: null,
            sampledFrames: n,
            stride: stride,
            // Structure of the side — what tells codec residue from real content.
            sideRho1: (sSide > 1e-18 && sPrevSide > 1e-18) ? sLag / Math.sqrt(sSide * sPrevSide) : 1,
            sideMidCorr: sMidSide / (Math.sqrt(sMid * sSide) + 1e-20),
            // Exact for files up to MAX_FRAMES; on a strided scan, a lower bound.
            sumPeak: sumPeak,
        };

        /* Silence analyses as "identical channels" on every statistic. It is
           not mono content, it is nothing — say so rather than inviting a
           conversion that cannot do anything. */
        if (programme <= 1e-7) {
            rep.kind = 'silent';
            rep.isMonoContent = false;
            rep.monoSumSafe = true;
            rep.polarityFlipped = false;
            return rep;
        }

        /* Between the floor gap and the old -60 dB line, the quieter channel
           is looked at over time — see the floor test above. */
        var quietIs = null;
        if (rep.quietChDb < QUIET_CH_DB) {
            quietIs = 'silent';
        } else if (rep.quietChDb < FLOOR_GAP_DB) {
            var env = (sl >= sr) ? envelopeOf(R, L, len, buffer.sampleRate) : envelopeOf(L, R, len, buffer.sampleRate);
            if (env) {
                rep.quietSpreadDb = env.spread;
                rep.quietEnvCorr = env.envCorr;
                if (env.spread < FLOOR_SPREAD_DB) quietIs = 'floor';
                else if (env.envCorr > BLEED_ENV_CORR) quietIs = 'bleed';
            }
        }

        if (quietIs) {
            rep.kind = 'one-sided';
            rep.loudSide = (sl >= sr) ? 'left' : 'right';
            rep.quietIs = quietIs;
        } else if (rep.bitExact || rep.sideDb < SIDE_MONO_DB) {
            rep.kind = 'dual-mono';
        } else if (rep.sideDb < STRUCT_SIDE_DB
                   && rep.sideRho1 < RESIDUE_RHO1
                   && Math.abs(rep.sideMidCorr) < RESIDUE_SM_CORR) {
            // Quiet, unstructured, and not a copy of the middle: codec residue
            // on two identical channels. Tagged so callers and telemetry can
            // tell this route from the bit-exact one.
            rep.kind = 'dual-mono';
            rep.codecResidue = true;
        } else {
            rep.kind = 'stereo';
        }

        rep.isMonoContent = (rep.kind === 'dual-mono' || rep.kind === 'one-sided');
        /* Heard from one side, whatever the quiet channel turned out to be —
           see "Lopsided" above. A one-sided file is lopsided too; a stereo one
           past the gap is the second voice (or stray bursts on a dead input). */
        rep.lopsided = rep.quietChDb < FLOOR_GAP_DB;
        if (rep.lopsided && rep.kind === 'stereo') {
            rep.quietIs = 'own';
            rep.loudSide = (sl >= sr) ? 'left' : 'right';
        }
        rep.polarityFlipped = rep.corr < POLARITY_CORR;
        rep.monoSumSafe = rep.sumLossDb >= SUM_LOSS_WARN_DB;
        return rep;
    }

    /**
     * The single mono signal a "this is really mono" file contains.
     * dual-mono → the average (identical channels, so this is also either one).
     * one-sided → the channel that actually carries the audio.
     * Returns null when the file has genuine stereo content, because then
     * there is no single correct answer and the caller must not guess.
     */
    function monoSignal(buffer, report) {
        if (!buffer || !report) return null;
        if (report.channels === 1) return buffer.getChannelData(0);
        if (report.kind === 'one-sided') return buffer.getChannelData(report.loudSide === 'left' ? 0 : 1);
        if (report.kind !== 'dual-mono') return null;
        var L = buffer.getChannelData(0), R = buffer.getChannelData(1);
        if (report.bitExact) return L;
        var n = Math.min(L.length, R.length), out = new Float32Array(n);
        for (var i = 0; i < n; i++) out[i] = (L[i] + R[i]) * 0.5;
        return out;
    }

    window.StereoContent = {
        analyse: analyse,
        monoSignal: monoSignal,
        THRESHOLDS: {
            SIDE_MONO_DB: SIDE_MONO_DB,
            QUIET_CH_DB: QUIET_CH_DB,
            FLOOR_GAP_DB: FLOOR_GAP_DB,
            FLOOR_SPREAD_DB: FLOOR_SPREAD_DB,
            BLEED_ENV_CORR: BLEED_ENV_CORR,
            SUM_LOSS_WARN_DB: SUM_LOSS_WARN_DB,
            POLARITY_CORR: POLARITY_CORR,
        },
    };
})();
